import { mdiArrowDown, mdiArrowUp } from '@mdi/js';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { NavLink, useBlocker } from 'react-router-dom';
import {
    CellProps,
    HeaderProps,
    Row,
    SortingRule,
    TableInstance,
    TableOptions,
    useAsyncDebounce,
    useExpanded,
    usePagination,
    useResizeColumns,
    useRowState,
    useSortBy,
    useTable,
} from 'react-table';
import { TooltipTransitionComponent } from '../base';
import {
    ActiveFilters,
    ColumnFilter,
    Filters,
    IColumnFiltersConfig,
    ICustomFilter,
    Loader,
    Pagination,
    Placeholder,
    Settings,
    TableExport,
} from './components';
import { RowSelectCell } from './components/cells/row-select';
import { DataTablePresenter } from './data-table.presenter';
import * as S from './data-table.styles';
import { useFlexLayout } from './plugins';
import { useReportVirtualRowSizes, useVirtualWindow } from './use-virtual-window';

/**
 * Observer selection checkboxes so checked state updates when `presenter.selectedIds`
 * changes — without putting `selectedIds` in `tableProps` deps (that rebuilds react-table
 * and freezes the UI on every checkbox click).
 */
const SelectionCheckboxHeader = observer(
    ({
        presenter,
        flatRows,
        hideSelectAll,
    }: {
        presenter: DataTablePresenter<any>;
        flatRows: Row<object>[];
        hideSelectAll?: boolean;
    }) => {
        if (hideSelectAll) {
            return null;
        }

        const { selectedIds, onSelectedUpdate } = presenter;

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const checked = event.target.checked;
            const columnIds: Record<string, boolean> = flatRows.reduce(
                (acc, row) => ({
                    ...acc,
                    [row.id]: checked,
                }),
                {},
            );
            const updatedSelectedIds = _.pickBy<boolean>({ ...selectedIds, ...columnIds }, val => val);
            onSelectedUpdate(updatedSelectedIds);
        };

        return (
            <RowSelectCell
                checked={flatRows.length > 0 && flatRows.every(row => selectedIds[row.id])}
                onChange={handleChange}
            />
        );
    },
);

const SelectionCheckboxCell = observer(
    ({
        rowId,
        presenter,
        showBorder,
    }: {
        rowId: string;
        presenter: DataTablePresenter<any>;
        showBorder?: boolean;
    }) => {
        const { selectedIds, onSelectedUpdate } = presenter;

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const checked = event.target.checked;
            const updatedSelectedIds = _.pickBy<boolean>({ ...selectedIds, [rowId]: checked }, val => val);
            onSelectedUpdate(updatedSelectedIds);
        };

        return <RowSelectCell checked={!!selectedIds[rowId]} showBorder={showBorder} onChange={handleChange} />;
    },
);

export interface ITableFetchOptions {
    pageIndex: number;
    pageSize: number;
    sortBy: SortingRule<object>[];
    validFilters: ICustomFilter[];
}

export interface IDataTableProps<D extends object = any> {
    className?: string;
    presenter: DataTablePresenter<any>;
    isLoading?: boolean;
    hideSettings?: boolean;
    hideNavigation?: boolean;
    hidePagination?: boolean;
    hideFiltering?: boolean;
    hideSelectAll?: boolean;
    hideHeader?: boolean;
    disableSorting?: boolean;
    selectableRows?: boolean;
    pageSizeOptions?: number[];
    showExport?: boolean;
    exportFilename?: string;
    summaryColumns?: string[];
    columnFiltersConfig?: IColumnFiltersConfig;
    onFetch?: (options: ITableFetchOptions) => void;
    onRowClick?: (row: Row<D>) => void;
    getRowHref?: (row: Row<D>) => string;
    renderRowSubComponent?: (row: Row<D>) => React.ReactElement | null;
    /**
     * Extra controls rendered at the END of the controls row, after pagination and settings.
     *
     * Receives the rendered rows, like `renderToolbar` — a control that acts on the table
     * (export, expand-all) cannot be written without them. Additive: an existing
     * `() => ReactElement` still satisfies this, because a function may ignore arguments.
     */
    renderControlsExtra?: (rows: Row<D>[], prepareRow: (row: Row<D>) => void) => React.ReactElement | null;
    /**
     * Controls rendered at the START of the controls row, beside the Filter button.
     *
     * `renderControlsExtra` puts things AFTER pagination and the column picker, which is the
     * wrong end for a search box. A screen that wants one control row rather than two needs a
     * leading slot, and forking the table for it would diverge from the other ~124 screens
     * that render this component (NS-1).
     *
     * Optional and inert: with the prop absent this renders nothing and the row is unchanged.
     */
    renderControlsLeading?: (rows: Row<D>[], prepareRow: (row: Row<D>) => void) => React.ReactElement | null;
    /**
     * Receives the rendered rows, matching `InfiniteScrollDataTable`. The two tables had
     * drifted: this one declared and called it with no arguments, so a toolbar could not
     * reach row state — expansion, selection — that the table already had in scope.
     * Additive; an existing `() => ReactElement` still satisfies it.
     */
    renderToolbar?: (rows: Row<D>[], prepareRow: (row: Row<D>) => void) => React.ReactElement;
    renderRowWrapper?: (row: Row<D>, renderRow: React.ReactElement) => React.ReactElement;
    rowClass?: (row: Row<D>) => string;
    NotFoundComponent?: React.ComponentType;
    onTableReady?: (tableInstance: TableInstance<D>) => void;
    /**
     * When true, only mount rows near the scroll viewport (estimate-based windowing).
     * Opt-in — default tables keep full DOM for compatibility.
     */
    virtualized?: boolean;
    /** Estimated row height in px when virtualized (default 56). */
    estimateRowHeight?: number;
}

const FETCH_DEBOUNCE = 500;

const defaultColumn = {
    minWidth: 50,
    width: 100,
    maxWidth: 400,
};

export const DataTable: React.FunctionComponent<IDataTableProps> = observer(props => {
    const {
        className,
        presenter,
        isLoading,
        hideSettings,
        hideNavigation,
        hideFiltering,
        hidePagination,
        hideSelectAll,
        hideHeader,
        disableSorting,
        selectableRows,
        pageSizeOptions,
        summaryColumns,
        columnFiltersConfig,
        onFetch,
        onRowClick,
        getRowHref,
        showExport,
        exportFilename,
        renderRowSubComponent,
        renderToolbar,
        renderControlsExtra,
        renderControlsLeading,
        renderRowWrapper,
        rowClass,
        NotFoundComponent,
        onTableReady,
        virtualized,
        estimateRowHeight,
    } = props;

    const defaultFetchOptions = React.useMemo(() => presenter.getTableDefaults(), [presenter]);

    const {
        columns,
        hiddenColumnIds,
        data,
        dataTotal,
        controlledPageSize,
        validFilters,
        selectedIds,
        saveTableDefaults,
        saveFilters,
        onColumnToggle,
        onPageSizeUpdate,
    } = presenter;

    const defaultSortColumn = columns.find(column => !!column.defaultSort);
    const defaultSort = defaultSortColumn?.defaultSort ? [defaultSortColumn.defaultSort] : [];

    const defaultSortValue = _.isEmpty(defaultFetchOptions?.sortBy) ? defaultSort : defaultFetchOptions?.sortBy;
    const sortByValue = disableSorting ? [] : defaultSortValue;

    // Memoize tableProps to regenerate rows only when we change values that can invoke rerender.
    // selectedIds must NOT be a dep — that rebuilds all react-table rows on every checkbox click.
    // Selection UI reads live state via observer SelectionCheckbox* cells instead.
    const tableProps: TableOptions<any> = useMemo(
        () => ({
            getRowId: (row: Row<any>, i: number) => row.id || i.toString(),
            defaultColumn: { ...defaultColumn },
            columns,
            data,
            initialState: {
                pageIndex: defaultFetchOptions?.pageIndex || 0,
                pageSize: controlledPageSize,
                sortBy: sortByValue,
                hiddenColumns: hiddenColumnIds,
            }, // Pass our hoisted table state
            useControlledState: state => {
                return React.useMemo(
                    () => ({
                        ...state,
                        pageSize: controlledPageSize,
                    }),
                    [state, controlledPageSize],
                );
            },
            pageCount: Math.ceil(dataTotal / controlledPageSize),
            manualSortBy: true,
            manualPagination: true,
            disableSortRemove: true,
            disableSortBy: disableSorting,
            autoResetExpanded: false,
            autoResetRowState: false,
        }),
        [
            data,
            dataTotal,
            controlledPageSize,
            columns,
            disableSorting,
            defaultFetchOptions,
            hiddenColumnIds,
        ],
    );

    const tableInstance = useTable(
        tableProps,
        useSortBy,
        useExpanded,
        usePagination,
        useResizeColumns,
        useFlexLayout,
        useRowState,
        hooks => {
            if (!selectableRows) {
                return;
            }

            hooks.visibleColumns.push(visibleColumns => [
                {
                    id: 'selection',
                    width: 'auto',
                    minWidth: 60,
                    Header: (headerProps: HeaderProps<object>) => (
                        <SelectionCheckboxHeader
                            presenter={presenter}
                            flatRows={headerProps.flatRows}
                            hideSelectAll={hideSelectAll}
                        />
                    ),
                    Cell: ({ row }: CellProps<object>) => {
                        const id = row.id || (row.original as { id?: string }).id;
                        if (!id) {
                            return null;
                        }
                        return <SelectionCheckboxCell rowId={id} presenter={presenter} showBorder={true} />;
                    },
                },
                ...visibleColumns,
            ]);
        },
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setHiddenColumns,
        state: { pageIndex, pageSize, sortBy, columnResizing } = tableInstance.state as any,
    } = tableInstance;

    const { nextPage, previousPage, gotoPage } = tableInstance as any;

    const summaryValues = useMemo(() => {
        if (!summaryColumns || summaryColumns.length === 0 || rows.length === 0) {
            return {};
        }

        const sums: Record<string, number> = {};

        summaryColumns.forEach(columnId => {
            const column = columns.find(col => col.id === columnId);
            if (!column || !column.accessor) {
                return;
            }

            const sum = rows.reduce((total, row) => {
                const value = _.get(row.original, column.accessor as string);
                
                let numericValue: number;
                if (value && typeof value === 'object' && 'amount' in value) {
                    numericValue = parseFloat(value.amount);
                } else if (typeof value === 'number') {
                    numericValue = value;
                } else {
                    numericValue = parseFloat(value);
                }
                
                return total + (isNaN(numericValue) ? 0 : numericValue);
            }, 0);

            sums[columnId] = sum;
        });

        return sums;
    }, [rows, summaryColumns, columns]);

    const handleChangePageSize = useCallback(
        (size: number) => {
            onPageSizeUpdate(size);
        },
        [onPageSizeUpdate],
    );

    useEffect(() => {
        setHiddenColumns(hiddenColumnIds);
    }, [hiddenColumnIds]);

    const hasCalledTableReady = useRef(false);
    useEffect(() => {
        if (onTableReady && !hasCalledTableReady.current) {
            hasCalledTableReady.current = true;
            onTableReady(tableInstance);
        }
    }, [onTableReady, tableInstance]);

    // Debounce our onFetchData call for 1000ms
    const handleFetchDebounce = onFetch && useAsyncDebounce(onFetch, FETCH_DEBOUNCE);

    const isFirstFetch = useRef(true);
    const fetchData = useCallback(
        (options: ITableFetchOptions): void => {
            if (!handleFetchDebounce) {
                return;
            }

            // For the first fetch we shouldn't use debounce
            if (isFirstFetch.current) {
                isFirstFetch.current = false;
                onFetch?.(options);
                return;
            }

            handleFetchDebounce(options);
        },
        [handleFetchDebounce, isFirstFetch],
    );

    // Reset the current page if filters were changed
    // to prevent staying out of the pages range.
    useEffect(() => {
        // Avoid handling on the first fetch as page can be stored
        if (isFirstFetch.current) {
            return;
        }
        gotoPage(0);
    }, [validFilters]);

    useEffect(() => {
        fetchData({
            pageIndex,
            pageSize,
            sortBy,
            validFilters,
        });
        saveTableDefaults({ pageIndex, pageSize, sortBy });
        saveFilters(validFilters);
    }, [pageIndex, pageSize, sortBy, validFilters]);

    const handleColumnFilterChange = useCallback(
        (filterId: string, filter: ICustomFilter | undefined) => {
            if (filterId === 'filterCreatedCombined' || filterId === 'filterUpdatedCombined') {
                const isCreated = filterId === 'filterCreatedCombined';
                const authorFilterId = 'filterAuthor';
                const dateFilterId = isCreated ? 'filterCreated' : 'filterUpdated';

                if (filter && filter.value) {
                    const combinedValue = filter.value as {
                        author?: { method: any; value: string };
                        date?: { method: any; value: Date };
                    };

                    presenter.findAndRemoveFilter({ filterId: authorFilterId });
                    presenter.findAndRemoveFilter({ filterId: dateFilterId });

                    if (combinedValue.author && combinedValue.author.value) {
                        presenter.onFilterAdd({
                            filterId: authorFilterId,
                            filterMethod: combinedValue.author.method,
                            filterType: 'String',
                            value: combinedValue.author.value,
                            label: isCreated ? 'Author' : 'Last Author',
                        } as ICustomFilter);
                    }

                    if (combinedValue.date && combinedValue.date.value) {
                        presenter.onFilterAdd({
                            filterId: dateFilterId,
                            filterMethod: combinedValue.date.method,
                            filterType: 'DateTime',
                            value: combinedValue.date.value,
                            label: isCreated ? 'Created' : 'Updated',
                        } as ICustomFilter);
                    }
                } else {
                    presenter.findAndRemoveFilter({ filterId: authorFilterId });
                    presenter.findAndRemoveFilter({ filterId: dateFilterId });
                }
            } else if (filter && Array.isArray(filter.value)) {
                if (filter.value.length > 0) {
                    const plainArray = _.cloneDeep(filter.value);
                    presenter.findAndUpdateFilters(
                        { filterId },
                        plainArray.map(
                            (option: any) =>
                                ({
                                    filterId,
                                    filterMethod: filter.filterMethod,
                                    filterType: filter.filterType,
                                    value: option,
                                    label: filter.label,
                                }) as ICustomFilter,
                        ),
                    );
                } else {
                    presenter.findAndRemoveFilter({ filterId });
                }
            } else {
                presenter.onColumnFilterChange(filterId, filter);
            }
        },
        [presenter],
    );

    const placeholder = dataTotal === 0 && !!isLoading;
    const loading = dataTotal > 0 && !!isLoading;
    // Match infinite-scroll: don't flash empty while a reload is pending.
    const emptyState = dataTotal === 0 && data.length === 0 && !isLoading;

    const isSelected = Object.values(selectedIds).some(id => !!id);

    // Safely use useBlocker: when rendered outside of a data router (e.g. inside a modal),
    // React Router throws. Guard with try/catch and fall back to a no-op blocker.
    let blocker: any;
    try {
        blocker = useBlocker(({ currentLocation, nextLocation }) =>
            isSelected && !!getRowHref && currentLocation.pathname !== nextLocation.pathname,
        );
    } catch (err) {
        blocker = { state: 'unblocked', proceed: () => {}, reset: () => {} };
    }

    useEffect(() => {
        if (blocker.state === "blocked") {
            if (window.confirm("There are some items in the table selected. Do you still want to navigate to the another page?")) {
                blocker.proceed?.();
            } else {
                blocker.reset?.();
            }
        }
    }, [blocker]);

    return (
        <S.TableWrapper className={className}>
            {!hideFiltering && (
                <>
                    <S.ActiveFiltersPanel>
                        <ActiveFilters presenter={presenter} />
                    </S.ActiveFiltersPanel>
                </>
            )}
            {(!hideFiltering || !hideNavigation || !hidePagination || !hideSettings) && (
                <S.ControlsPanel>
                    {!hideFiltering && !columnFiltersConfig && <Filters presenter={presenter} />}
                    {renderControlsLeading?.(rows, prepareRow)}
                    {!hideNavigation && (
                        <S.NavigationContainer>
                            {!hidePagination && (
                                <Pagination
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    dataTotal={dataTotal}
                                    disabled={isLoading}
                                    pageSizeOptions={pageSizeOptions}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    gotoPage={gotoPage}
                                    onChangePageSize={handleChangePageSize}
                                />
                            )}
                            {!hideSettings && (
                                <Settings
                                    columns={columns}
                                    hiddenColumnIds={hiddenColumnIds}
                                    onColumnToggle={onColumnToggle}
                                    variant="small"
                                />
                            )}
                            {!!showExport && <TableExport rows={rows} prepareRow={prepareRow} filename={exportFilename} />}
                            {renderControlsExtra?.(rows, prepareRow)}
                        </S.NavigationContainer>
                    )}
                </S.ControlsPanel>
            )}
            {renderToolbar?.(rows, prepareRow)}
            {placeholder ? (
                <Placeholder />
            ) : (
                <S.TableContainer data-table-scroll-root={virtualized ? 'true' : undefined}>
                    {loading && <Loader loading={loading} />}
                    <S.Table {...getTableProps()}>
                        {!hideHeader && (
                            <S.TableHead>
                                {headerGroups.map((headerGroup, hi) => (
                                    <S.TableRow {...headerGroup.getHeaderGroupProps()} key={hi}>
                                        {headerGroup.headers.map((column, ci) => {
                                            const description = _.get(column, 'description');
                                            const columnId = column.id;
                                            const hasColumnFilter =
                                                columnFiltersConfig && columnId && columnFiltersConfig[columnId];
                                            const filterId = hasColumnFilter
                                                ? columnFiltersConfig[columnId].filterId || columnId
                                                : columnId;
                                            let currentFilter = hasColumnFilter
                                                ? presenter.getColumnFilter(filterId)
                                                : undefined;

                                            if (
                                                !currentFilter &&
                                                (filterId === 'filterCreatedCombined' ||
                                                    filterId === 'filterUpdatedCombined')
                                            ) {
                                                const isCreated = filterId === 'filterCreatedCombined';
                                                const authorFilterId = 'filterAuthor';
                                                const dateFilterId = isCreated ? 'filterCreated' : 'filterUpdated';

                                                const authorFilter = presenter.getColumnFilter(authorFilterId);
                                                const dateFilter = presenter.getColumnFilter(dateFilterId);

                                                if (authorFilter || dateFilter) {
                                                    currentFilter = {
                                                        filterId: filterId,
                                                        filterMethod: undefined,
                                                        filterType: 'String',
                                                        value: {
                                                            author: authorFilter
                                                                ? {
                                                                      method: authorFilter.filterMethod,
                                                                      value: authorFilter.value,
                                                                  }
                                                                : undefined,
                                                            date: dateFilter
                                                                ? {
                                                                      method: dateFilter.filterMethod,
                                                                      value: dateFilter.value,
                                                                  }
                                                                : undefined,
                                                        },
                                                    } as any as ICustomFilter;
                                                }
                                            }

                                            return (
                                                <S.TableHeader
                                                    canSort={(column as any).canSort}
                                                    isSorted={(column as any).isSorted}
                                                    data-sorted={(column as any).isSorted ? 'true' : 'false'}
                                                    {...column.getHeaderProps()}
                                                    key={ci}
                                                    className="table-header-with-filter">
                                                    <div {...(column as any).getSortByToggleProps({ title: undefined })}>
                                                        <Tooltip
                                                            placement="top"
                                                            TransitionComponent={TooltipTransitionComponent}
                                                            title={<span>{description}</span>}
                                                            open={description ? undefined : false}
                                                            enterTouchDelay={0}>
                                                            <span>{column.render('Header')}</span>
                                                        </Tooltip>
                                                        {hasColumnFilter ? (
                                                            <ColumnFilter
                                                                columnId={columnId}
                                                                columnLabel={
                                                                    typeof column.Header === 'string'
                                                                        ? column.Header
                                                                        : columnId
                                                                }
                                                                filterConfig={columnFiltersConfig[columnId]}
                                                                currentFilter={currentFilter}
                                                                onApply={filter =>
                                                                    handleColumnFilterChange(filterId, filter)
                                                                }
                                                            />
                                                        ) : (
                                                            columnFiltersConfig &&
                                                            columnId !== 'selection' && (
                                                                <S.HeaderFilterSpacer aria-hidden />
                                                            )
                                                        )}
                                                        {(column as any).isSorted && (
                                                            <SortIcon isSortedDesc={!!(column as any).isSortedDesc} />
                                                        )}
                                                    </div>

                                                    <div
                                                        {...(column as any).getResizerProps()}
                                                        className={`resizer ${(column as any).isResizing ? 'isResizing' : ''}`}
                                                    />
                                                </S.TableHeader>
                                            );
                                        })}
                                    </S.TableRow>
                                ))}
                            </S.TableHead>
                        )}
                        <S.TableBody {...getTableBodyProps()}>
                            <TableBody
                                rows={rows}
                                prepareRow={prepareRow}
                                onRowClick={onRowClick}
                                getRowHref={getRowHref}
                                renderRowSubComponent={renderRowSubComponent}
                                renderRowWrapper={renderRowWrapper}
                                rowClass={rowClass}
                                isSelected={isSelected}
                                columnResizing={columnResizing}
                                summaryColumns={summaryColumns}
                                summaryValues={summaryValues}
                                headerGroups={headerGroups}
                                virtualized={!!virtualized}
                                estimateRowHeight={estimateRowHeight || 56}
                            />
                        </S.TableBody>
                    </S.Table>
                </S.TableContainer>
            )}
            {emptyState && (NotFoundComponent ? <NotFoundComponent /> : <EmptyState />)}
        </S.TableWrapper>
    );
});

type TableProps = Pick<TableInstance, 'rows' | 'prepareRow'> &
    Pick<IDataTableProps, 'onRowClick' | 'getRowHref' | 'renderRowSubComponent' | 'renderRowWrapper' | 'rowClass' | 'summaryColumns'> & {
        // Determines if any item in the table is selected
        isSelected?: boolean;
        columnResizing: any;
        summaryValues: Record<string, number>;
        headerGroups: any[];
        virtualized?: boolean;
        estimateRowHeight?: number;
    };

const TableBody = React.memo<TableProps>(props => {
    const {
        rows,
        onRowClick,
        getRowHref,
        renderRowSubComponent,
        renderRowWrapper,
        rowClass,
        prepareRow,
        summaryColumns,
        summaryValues,
        headerGroups,
        virtualized,
        estimateRowHeight = 56,
    } = props;
    const handleRowClick = (row: Row) => () => onRowClick?.(row);
    const handleRowClass = (row: Row) => (rowClass ? rowClass(row) : undefined);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [measureRootEl, setMeasureRootEl] = React.useState<HTMLElement | null>(null);

    const showSummary = summaryColumns && summaryColumns.length > 0 && rows.length > 0;
    const { reportSizes, ...virtualWindow } = useVirtualWindow({
        enabled: !!virtualized,
        count: rows.length,
        estimateSize: estimateRowHeight,
        overscan: 10,
        anchorEl,
    });

    useReportVirtualRowSizes(
        !!virtualized,
        measureRootEl,
        virtualWindow.startIndex,
        virtualWindow.endIndex,
        reportSizes,
    );

    function renderRow(row: Row<{}>) {
        const hoverable = !!onRowClick || !!getRowHref;
        const { key, ...rowProps } = row.getRowProps();
        return (
            <React.Fragment>
                <S.TableRow
                    key={key}
                    className={handleRowClass(row)}
                    onClick={handleRowClick ? handleRowClick(row) : undefined}
                    hoverable={hoverable}
                    {...rowProps}>
                    {row.cells.map((cell, ci) => {
                        return (
                            <S.TableCell {...cell.getCellProps()} key={ci}>
                                {cell.render('Cell')}
                            </S.TableCell>
                        );
                    })}
                </S.TableRow>
                {(row as any).isExpanded && <S.TableRow>{renderRowSubComponent && renderRowSubComponent(row)}</S.TableRow>}
            </React.Fragment>
        );
    }

    const visibleRows = virtualized
        ? rows.slice(virtualWindow.startIndex, virtualWindow.endIndex)
        : rows;

    const rowNodes = visibleRows.map((row, ri) => {
        prepareRow(row);
        const absoluteIndex = virtualized ? virtualWindow.startIndex + ri : ri;
        const key = row.id || String(absoluteIndex);
        const body = getRowHref ? (
            <NavLink to={getRowHref(row)}>
                {!!renderRowWrapper ? renderRowWrapper(row, renderRow(row)) : renderRow(row)}
            </NavLink>
        ) : !!renderRowWrapper ? (
            renderRowWrapper(row, renderRow(row))
        ) : (
            renderRow(row)
        );

        if (!virtualized) {
            return <React.Fragment key={key}>{body}</React.Fragment>;
        }

        return (
            <div key={key} data-virtual-index={absoluteIndex}>
                {body}
            </div>
        );
    });

    return (
        <div ref={setAnchorEl}>
            {virtualized ? (
                <div
                    style={{
                        height: virtualWindow.totalHeight,
                        position: 'relative',
                        width: '100%',
                    }}
                >
                    <div
                        ref={setMeasureRootEl}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translate3d(0, ${virtualWindow.offsetY}px, 0)`,
                            willChange: 'transform',
                        }}
                    >
                        {rowNodes}
                    </div>
                </div>
            ) : (
                rowNodes
            )}
            {showSummary && <SummaryRow headerGroups={headerGroups} summaryValues={summaryValues} />}
        </div>
    );
});

const SummaryRow = React.memo<{
    headerGroups: any[];
    summaryValues: Record<string, number>;
}>(({ headerGroups, summaryValues }) => {
    // Get the first header group for column structure
    const headerGroup = headerGroups[0];
    if (!headerGroup) {
        return null;
    }

    const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
    return (
        <S.TableSummaryRow key={key} {...headerGroupProps}>
            {headerGroup.headers.map((column: any, ci: number) => {
                const columnId = column.id;
                const summaryValue = summaryValues[columnId];
                
                // Currency unless the column says otherwise. This row used to format EVERY summed column as
                // dollars, which stayed invisible while only money was ever summed — the first count column
                // (the denials table's Adjustments) rendered 1,272 adjustments as "$1272.00".
                let displayValue: React.ReactNode = column.summaryLabel ? (
                    <S.TableSummaryLabel>{column.summaryLabel}</S.TableSummaryLabel>
                ) : (
                    ''
                );
                if (summaryValue !== undefined) {
                    displayValue = column.summaryFormat
                        ? column.summaryFormat(summaryValue)
                        : `$${summaryValue.toFixed(2)}`;
                }

                const { key: colKey, ...colHeaderProps } = column.getHeaderProps();
                return (
                    <S.TableSummaryCell key={colKey} {...colHeaderProps}>
                        {displayValue}
                    </S.TableSummaryCell>
                );
            })}
        </S.TableSummaryRow>
    );
});

const EmptyState = React.memo(() => (
    <S.EmptyContainer>
        <S.EmptyTitle>Nothing to show</S.EmptyTitle>
    </S.EmptyContainer>
));

const SortIcon = React.memo<{ isSortedDesc: boolean }>(({ isSortedDesc }) =>
    isSortedDesc ? <S.SortIcon path={mdiArrowDown} size={0.5} /> : <S.SortIcon path={mdiArrowUp} size={0.5} />,
);
